import {
  getFallbackSelectionForInterval,
  getFallbackSelectionForPlan,
  getPlanByTier,
  normalizeRegistrationPlans,
  resolveInitialRegistrationSelection,
  type RegistrationBillingInterval,
  type RegistrationPlanApiResponse,
  type RegistrationPlanOption,
  type RegistrationPlanTier,
  type RegistrationSelection
} from '@dashboard/modules/user/services/registrationPlans'

export interface AgencyPlanApiResponse extends RegistrationPlanApiResponse {
  monthly_pricing_token: string | null
  yearly_pricing_token: string | null
}

export interface AgencyPlanOption extends RegistrationPlanOption {
  monthlyPricingToken: string | null
  yearlyPricingToken: string | null
}

export function normalizeAgencyPlanOptions(plans: AgencyPlanApiResponse[]): AgencyPlanOption[] {
  const normalizedPlans = normalizeRegistrationPlans(plans)

  return normalizedPlans.map((plan) => {
    const sourcePlan = plans.find((candidate) => candidate.tier === plan.tier)

    return {
      ...plan,
      monthlyPricingToken: sourcePlan?.monthly_pricing_token ?? null,
      yearlyPricingToken: sourcePlan?.yearly_pricing_token ?? null
    }
  })
}

export function getAgencyPricingToken(
  plan: AgencyPlanOption | null | undefined,
  billingInterval: RegistrationBillingInterval | null | undefined
): string | null {
  if (!plan || !billingInterval) {
    return null
  }

  return billingInterval === 'yearly' ? plan.yearlyPricingToken : plan.monthlyPricingToken
}

export function resolveAgencyPlanDefaultSelection(
  plans: AgencyPlanOption[],
  defaultTier: RegistrationPlanTier | null | undefined,
  defaultInterval: RegistrationBillingInterval | null | undefined
): RegistrationSelection | null {
  if (!plans.length) {
    return null
  }

  const requested = resolveInitialRegistrationSelection(plans, defaultTier, defaultInterval)
  if (requested) {
    return requested
  }

  const defaultPlan = getPlanByTier(plans, defaultTier)
  if (defaultPlan) {
    return getFallbackSelectionForPlan(defaultPlan)
  }

  if (defaultInterval === 'monthly' || defaultInterval === 'yearly') {
    const intervalFallback = getFallbackSelectionForInterval(plans, defaultInterval)
    if (intervalFallback) {
      return intervalFallback
    }
  }

  return getFallbackSelectionForInterval(plans, 'monthly') ?? getFallbackSelectionForInterval(plans, 'yearly')
}

export function buildAgencySignupUrl(baseUrl: string, pricingToken: string | null | undefined): string {
  const trimmedToken = pricingToken?.trim()
  if (!trimmedToken) {
    return baseUrl
  }

  try {
    const parsed = new URL(baseUrl)
    parsed.searchParams.set('pricing_token', trimmedToken)
    return parsed.toString()
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}pricing_token=${encodeURIComponent(trimmedToken)}`
  }
}
